import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { checkCheckoutStatus } from '../lib/api';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

export default function SubscriptionSuccess() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setMessage('Invalid session. Please try again.');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const result = await checkCheckoutStatus(sessionId);
      
      if (result.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your subscription is now active.');
        return;
      } else if (result.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      // Keep polling on error
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <Card className={`max-w-md w-full ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <CardHeader className="text-center">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            status === 'loading' 
              ? (isDark ? 'bg-blue-500/20' : 'bg-blue-100')
              : status === 'success'
              ? (isDark ? 'bg-green-500/20' : 'bg-green-100')
              : (isDark ? 'bg-red-500/20' : 'bg-red-100')
          }`}>
            {status === 'loading' && (
              <Loader2 className={`h-8 w-8 animate-spin ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            )}
            {status === 'success' && (
              <CheckCircle className={`h-8 w-8 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            )}
            {status === 'error' && (
              <XCircle className={`h-8 w-8 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            )}
          </div>
          <CardTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {status === 'loading' && 'Processing Payment'}
            {status === 'success' && 'Welcome to Doc RVU Tracker!'}
            {status === 'error' && 'Payment Issue'}
          </CardTitle>
          <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {message}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="flex justify-center pt-4">
          {status === 'success' && (
            <Button
              onClick={() => navigate('/rvu-tracker')}
              className={isDark 
                ? 'bg-green-600 hover:bg-green-500 text-white' 
                : 'bg-green-600 hover:bg-green-500 text-white'
              }
            >
              Go to Tracker <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
          {status === 'error' && (
            <div className="space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/subscription')}
                className={isDark ? 'border-slate-600 text-slate-300' : ''}
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/rvu-tracker')}
                className={isDark 
                  ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                  : 'bg-gray-600 hover:bg-gray-500 text-white'
                }
              >
                Go to App
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
